import type Hls from 'hls.js';
import { type StateCreator } from 'zustand';

import { PlayerTimeFormat } from '../constants/timeFormat';

export type ResolutionLevel = {
  height: number;
  id: number;
  width: number;
};

export type SubtitleTrack = {
  id: number;
  lang?: string;
  name?: string;
};

export type PlayerSlice = {
  currentTime: number;
  duration: number;
  hlsInstance: Hls | null;
  id: string;
  initialized: boolean;
  loop: boolean;
  muted: boolean;
  playbackRate: number;
  playing: boolean;
  ready: boolean;
  reset: (id: string) => void;
  resolutionLevel: number;
  resolutionLevels: ResolutionLevel[];
  seeking: boolean;
  setCurrentTime: (currentTime: number) => void;
  setDuration: (duration: number) => void;
  setHlsInstance: (hlsInstance: Hls | null) => void;
  setInitialized: (initialized: boolean) => void;
  setLoop: (loop: boolean) => void;
  setMuted: (muted: boolean) => void;
  setPlaying: (playing: boolean) => void;
  setPlaybackRate: (playbackRate: number) => void;
  setReady: (ready: boolean) => void;
  setResolutionLevel: (resolutionLevel: number) => void;
  setResolutionLevels: (resolutionLevels: ResolutionLevel[]) => void;
  setSeeking: (seeking: boolean) => void;
  setSubtitleTrack: (subtitleTrack: number) => void;
  setSubtitleTracks: (subtitleTracks: SubtitleTrack[]) => void;
  setVideoElement: (videoElement: HTMLVideoElement | null) => void;
  setVolume: (volume: number) => void;
  subtitleTrack: number;
  subtitleTracks: SubtitleTrack[];
  timeFormat: PlayerTimeFormat;
  setTimeFormat: (timeFormat: PlayerTimeFormat) => void;
  videoElement: HTMLVideoElement | null;
  volume: number;
};

export type PlayerSliceInitialState = Pick<PlayerSlice, 'id'> &
  Pick<
    Partial<PlayerSlice>,
    | 'currentTime'
    | 'duration'
    | 'hlsInstance'
    | 'initialized'
    | 'loop'
    | 'muted'
    | 'playbackRate'
    | 'playing'
    | 'ready'
    | 'resolutionLevel'
    | 'resolutionLevels'
    | 'seeking'
    | 'subtitleTrack'
    | 'subtitleTracks'
    | 'timeFormat'
    | 'videoElement'
    | 'volume'
  >;

const INITIAL_PLAYER_STATE = {
  currentTime: 0,
  duration: 0,
  hlsInstance: null,
  initialized: false,
  loop: false,
  muted: false,
  playbackRate: 1,
  playing: false,
  ready: false,
  resolutionLevel: -1,
  resolutionLevels: [],
  seeking: false,
  subtitleTrack: -1,
  subtitleTracks: [],
  timeFormat: PlayerTimeFormat.Standard,
  videoElement: null,
  volume: 1,
} satisfies Omit<PlayerSliceInitialState, 'id'>;

export type CreatePlayerSlice = (
  initialState: PlayerSliceInitialState,
) => StateCreator<PlayerSlice, [], [], PlayerSlice>;

export const createPlayerSlice: CreatePlayerSlice = (initialState) => (set, _get, store) => {
  const mergedInitialState = {
    ...INITIAL_PLAYER_STATE,
    ...initialState,
  };

  return {
    ...mergedInitialState,
    reset: (id) => {
      const initialState = store.getInitialState();

      return set((state) => ({
        ...state,
        ...initialState,
        id,
        muted: state.muted,
        playbackRate: state.playbackRate,
        resolutionLevel: state.resolutionLevel,
        timeFormat: state.timeFormat,
        volume: state.volume,
      }));
    },
    setCurrentTime: (currentTime) => set({ currentTime }),
    setDuration: (duration) => set({ duration }),
    setHlsInstance: (hlsInstance) => set({ hlsInstance }),
    setInitialized: (initialized) => set({ initialized }),
    setLoop: (loop) => set({ loop }),
    setMuted: (muted) => set({ muted }),
    setPlaybackRate: (playbackRate) => set({ playbackRate }),
    setPlaying: (playing) => set({ playing }),
    setReady: (ready) => set({ ready }),
    setResolutionLevel: (resolutionLevel) => set({ resolutionLevel }),
    setResolutionLevels: (resolutionLevels) => set({ resolutionLevels }),
    setSeeking: (seeking) => set({ seeking }),
    setSubtitleTrack: (subtitleTrack) => set({ subtitleTrack }),
    setSubtitleTracks: (subtitleTracks) => set({ subtitleTracks }),
    setTimeFormat: (timeFormat) => set({ timeFormat }),
    setVideoElement: (videoElement) => set({ videoElement }),
    setVolume: (volume) => set({ volume }),
  };
};
